import { subject } from '@casl/ability';
import { Stack } from '@mui/material';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { StyledDivider } from '@/components/common/StyledDivider';
import { DetailSidebarContent } from '@/components/map/details/DetailSidebarContent';
import { RmvStationLink } from '@/components/map/details/RmvStationLink';
import { ParkAndRideFacilityDetailContent } from '@/components/map/details/content/ParkAndRideFacilityDetailContent';
import { ParkingFacilityDetailTable } from '@/components/map/details/table/ParkingFacilityDetailTable';
import { ButtonLink } from '@/components/router/ButtonLink';
import { useAbility } from '@/hooks/useAbility';
import { I18N_DE } from '@/i18n/de';
import { Action, Subject } from '@/modules/api/backend';
import {
  backendApiParkAndRideFacilitiesFindOneOptions,
  backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { MapLayer } from '@/types/MapLayer';

export const Route = createFileRoute('/_public/_map/_sidebar/park-and-ride-facilities/$parkAndRideFacilityId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { parkAndRideFacilityId } = Route.useParams();
  const { ability } = useAbility();

  const { data: facility } = useSuspenseQuery({
    ...backendApiParkAndRideFacilitiesFindOneOptions({
      path: { parkAndRideFacilityId },
      query: { station: true, organization: true },
    }),
  });

  const { data: parkingData } = useQuery({
    ...backendApiParkingDataFindAllLatestParkAndRideFacilityDataOptions(),
    select: (data) => data.find((item) => item.id === parkAndRideFacilityId),
  });

  return (
    <DetailSidebarContent
      mapLayer={facility.stationId ? MapLayer.PARK_AND_RIDE_FACILITY : MapLayer.PARKING_FACILITY}
      subtitle={facility?.facilityType ? I18N_DE.FacilityType[facility.facilityType] : undefined}
      title={facility.name}
    >
      <ParkAndRideFacilityDetailContent data={parkingData} facility={facility} />

      <StyledDivider />

      <ParkingFacilityDetailTable facility={facility} />

      {facility.station ? <RmvStationLink station={facility.station} /> : null}

      <Stack alignItems="flex-end">
        {facility &&
        ability?.can(Action.UPDATE, subject(Subject.ParkAndRideFacility, facility)) &&
        facility.stationId ? (
          <ButtonLink
            color="primary"
            onClick={() => {}}
            params={{ stationId: facility.stationId, parkAndRideFacilityId: facility.id }}
            to="/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId"
            variant="contained"
          >
            Bearbeiten
          </ButtonLink>
        ) : null}
      </Stack>
    </DetailSidebarContent>
  );
}
